import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { AuditLoggers } from '../../../utils/auditLogger';
import UserDrawer from './UserDrawer';
import GLSystemDrawer from './GLSystemDrawer';
import TenantWorkforceWeekSection from './TenantWorkforceWeekSection';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateTabsSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateBlueButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { DOC_PARSER, ALTERNATIVE_PARSER, isAlternativeParser, normalizeParserType, parserTypeLabel } from '../../../utils/parserTypes';

const DocumentTypesDrawer = ({ open, onClose, tenantId }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.TENANT);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const tenantName = localStorage.getItem('tenant_name') || 'Unknown Tenant';

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState('docTypes');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    username: ''
  });
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('WF');
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowData, setWorkflowData] = useState([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [editWorkflowData, setEditWorkflowData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [docTypeDeleteConfirmOpen, setDocTypeDeleteConfirmOpen] = useState(false);
  const [docTypeConfigToDelete, setDocTypeConfigToDelete] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  const [newWorkflow, setNewWorkflow] = useState({
    workflowDefinitionSnowflakeId: '',
    parserType: '',
    parserKey: '',
    parserSegmentSize: ''
  });
  const [formError, setFormError] = useState('');
  const [userRefreshTrigger, setUserRefreshTrigger] = useState(false);
  const glSystemDrawerRef = useRef(null);
  const [selectedGLSystemRow, setSelectedGLSystemRow] = useState(null);

  const fetchGrid = async (category = 'WF') => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      // Use the new API endpoint with category query parameter
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}`);
      url.searchParams.append('category', category);
      
      console.log(`Fetching document types for category: ${category}`, url.toString());
      
      const resp = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        const errorData = await resp.text();
        console.error(`Error response: ${resp.status}`, errorData);
        throw new Error(`Failed to fetch ${category} document types: ${resp.status} ${resp.statusText}`);
      }
      
      const data = await resp.json();
      console.log('API Response data:', data);
      
      // Ensure we have an array and set the rows
      const rowsData = Array.isArray(data) ? data : [];
      console.log(`Setting ${rowsData.length} rows for category ${category}`, rowsData);
      setRows(rowsData);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered with:', { open, tenantId, activeTab });
    if (!open || !tenantId) {
      console.log('Skipping fetch: open =', open, 'tenantId =', tenantId);
      return;
    }
    console.log('Fetching data for tab:', activeTab);
    fetchGrid(activeTab);
  }, [open, tenantId, activeTab]);

  const handleTabChange = (event, newValue) => {
    console.log('Tab changed from', activeTab, 'to', newValue);
    setActiveTab(newValue);
    // Fetch data for the newly selected tab
    fetchGrid(newValue);
  };

  const handleOpenDialog = async () => {
    if (!canAdd) return;
    setDialogOpen(true);
    setSelectedDocType('');
    // Fetch document types for dropdown filtered by active tab
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/${activeTab}`);
      const resp = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': `application/json`,
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch ${activeTab} document types`);
      const data = await resp.json();
      setDocTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setDocTypes([]);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDocType('');
  };

  const handleOpenUserDialog = () => {
    if (!canAdd) return;
    setUserDialogOpen(true);
    setUserForm({ email: '', first_name: '', last_name: '', username: '' });
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setUserForm({ email: '', first_name: '', last_name: '', username: '' });
  };

  const handleUserFormChange = (e) => {
    const newValue = e.target.value;
    // If email is being updated, also update username with the same value
    if (e.target.name === 'email') {
      setUserForm({ ...userForm, email: newValue, username: newValue });
    } else {
      setUserForm({ ...userForm, [e.target.name]: newValue });
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setUserSubmitting(true);
    try {
      const payload = { ...userForm, tenant_id: tenantId };
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/users`;
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to add user';
        
        // Log failed user creation
        await AuditLoggers.userCreated(
          userForm.email, 
          tenantName, 
          false, 
          errorMessage
        );
        
        throw new Error(errorMessage);
      }
      
      // Log successful user creation
      await AuditLoggers.userCreated(userForm.email, tenantName);
      
      handleCloseUserDialog();
      // Toggle refresh trigger to force UserDrawer to refresh the user list
      setUserRefreshTrigger(prev => !prev);
    } catch (err) {
      console.error('Error saving user:', err);
      alert(err.message || 'Failed to add user');
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedDocType) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': `application/json`,
        },
        body: JSON.stringify({
          tenantId: tenantId,
          documentTypeId: selectedDocType,
          includeTerms: [],
          excludeTerms: [],
        }),
      });
      if (!resp.ok) throw new Error('Failed to add document type config');
      handleCloseDialog();
      fetchGrid(activeTab); // Pass the current active tab to ensure correct data refresh
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchWorkflowData = async (documentTypeId) => {
    setWorkflowLoading(true);
    setWorkflowError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${documentTypeId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow data: ${response.status}`);
      }

      const data = await response.json();
      setWorkflowData(Array.isArray(data) ? data : [data]);
      setWorkflowDialogOpen(true);
    } catch (err) {
      setWorkflowError(err.message);
      console.error('Error fetching workflow data:', err);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleWorkflowInfoClick = (documentType) => {
    setSelectedDocumentType(documentType);
    fetchWorkflowData(documentType.documentTypeId);
  };

  const handleCloseWorkflowDialog = () => {
    setWorkflowDialogOpen(false);
    setWorkflowData([]);
    setSelectedDocumentType(null);
  };

  const handleEditWorkflow = (workflow) => {
    if (!canEdit) return;
    setFormError('');
    setEditWorkflowData({
      ...workflow,
      parserType: normalizeParserType(workflow.parserType),
    });
    setEditDialogOpen(true);
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!canDelete) return;
    setWorkflowToDelete(workflowId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    
    setDeleteConfirmOpen(false);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/${workflowToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete workflow: ${response.status}`);
      }

      // Refresh the workflow data
      if (selectedDocumentType) {
  fetchWorkflowData(selectedDocumentType.documentTypeId);
      }
      showSnackbar('Workflow deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting workflow:', err);
      showSnackbar(`Failed to delete workflow: ${err.message}`, 'error');
    }
  };

  const handleSaveWorkflow = async (e) => {
    e.preventDefault();
    if (!editWorkflowData) return;

    if (isAlternativeParser(editWorkflowData.parserType) && !String(editWorkflowData.parserKey || '').trim()) {
      setFormError('Parser Key is required for Alternative Parser');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const alternative = isAlternativeParser(editWorkflowData.parserType);
      const body = {
        ...editWorkflowData,
        parserType: normalizeParserType(editWorkflowData.parserType),
        parserKey: alternative ? String(editWorkflowData.parserKey || '').trim() : '',
        parserSegmentSize: alternative && editWorkflowData.parserSegmentSize !== '' && editWorkflowData.parserSegmentSize != null
          ? parseInt(editWorkflowData.parserSegmentSize, 10)
          : null,
      };
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/${editWorkflowData.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update workflow: ${response.status}`);
      }

      // Refresh the workflow data
      if (selectedDocumentType) {
  fetchWorkflowData(selectedDocumentType.documentTypeId);
      }
      
      setEditDialogOpen(false);
      setEditWorkflowData(null);
      showSnackbar('Workflow updated successfully', 'success');
    } catch (err) {
      console.error('Error updating workflow:', err);
      showSnackbar(`Failed to update workflow: ${err.message}`, 'error');
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditWorkflowData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'parserType' && value !== ALTERNATIVE_PARSER
        ? { parserKey: '', parserSegmentSize: '' }
        : {}),
    }));
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setWorkflowToDelete(null);
  };

  const handleDeleteDocTypeConfig = (row) => {
    if (!row?.id) {
      showSnackbar('Unable to delete: missing config id', 'error');
      return;
    }
    setDocTypeConfigToDelete(row);
    setDocTypeDeleteConfirmOpen(true);
  };

  const handleCancelDocTypeDelete = () => {
    setDocTypeDeleteConfirmOpen(false);
    setDocTypeConfigToDelete(null);
  };

  const confirmDeleteDocTypeConfig = async () => {
    if (!docTypeConfigToDelete?.id) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Prevent deleting doc type config when workflows are still attached.
      const workflowCheckResp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${docTypeConfigToDelete.documentTypeId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!workflowCheckResp.ok) {
        throw new Error(`Failed to verify attached workflows: ${workflowCheckResp.status}`);
      }

      const workflowCheckData = await workflowCheckResp.json();
      const hasAttachedWorkflows =
        (Array.isArray(workflowCheckData) && workflowCheckData.length > 0) ||
        (!Array.isArray(workflowCheckData) && workflowCheckData && Object.keys(workflowCheckData).length > 0);

      if (hasAttachedWorkflows) {
        showSnackbar('Please delete attached workflow(s) first before deleting this document type.', 'warning');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/${docTypeConfigToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete document type config: ${response.status}`);
      }

      await fetchGrid(activeTab);
      handleCancelDocTypeDelete();
      showSnackbar('Document type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting document type config:', err);
      showSnackbar(`Failed to delete document type: ${err.message}`, 'error');
    }
  };

  const fetchWorkflowDefinitions = async (documentTypeId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/document-type/${documentTypeId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow definitions: ${response.status}`);
      }

      const data = await response.json();
      setWorkflowDefinitions(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      console.error('Error fetching workflow definitions:', err);
      showSnackbar(`Failed to load workflow definitions: ${err.message}`, 'error');
      return [];
    }
  };

  const handleOpenAddDialog = async () => {
    if (!canAdd) return;
    // Reset form state
    setNewWorkflow({
      workflowDefinitionSnowflakeId: '',
      parserType: '',
      parserKey: '',
      parserSegmentSize:''
    });
    setFormError('');
    
    if (selectedDocumentType) {
      await fetchWorkflowDefinitions(selectedDocumentType.documentTypeId);
    }
    setAddDialogOpen(true);
  };

  const handleAddWorkflow = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!selectedDocumentType) return;
    
    // Validate required fields
    if (!newWorkflow.workflowDefinitionSnowflakeId || !newWorkflow.parserType) {
      setFormError('Please fill in all required fields');
      return;
    }
    if (isAlternativeParser(newWorkflow.parserType) && !String(newWorkflow.parserKey || '').trim()) {
      setFormError('Parser Key is required for Alternative Parser');
      return;
    }
    
    try {
      setSubmitting(true);
      const alternative = isAlternativeParser(newWorkflow.parserType);
      const payload = {
        tenantId: tenantId,
        documentTypeId: selectedDocumentType.documentTypeId,
        workflowDefinitionSnowflakeId: newWorkflow.workflowDefinitionSnowflakeId.trim(),
        parserType: normalizeParserType(newWorkflow.parserType),
        parserKey: alternative ? newWorkflow.parserKey.trim() : '',
        ...(alternative && newWorkflow.parserSegmentSize
          ? { parserSegmentSize: parseInt(newWorkflow.parserSegmentSize, 10) }
          : {}),
      };
      
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.message && errorData.message.includes('already exists')) {
          setFormError('A workflow with these details already exists for this document type');
          return;
        } else {
          setFormError(errorData.message || `Failed to add workflow: ${response.status}`);
          return;
        }
      }
      
      fetchWorkflowData(selectedDocumentType.documentTypeId);
      setAddDialogOpen(false);
      setNewWorkflow({
        workflowDefinitionSnowflakeId: '',
        parserType: '',
        parserKey: '',
        parserSegmentSize: ''
      });
      showSnackbar('Workflow added successfully', 'success');
    } catch (error) {
      console.error('Error adding workflow:', error);
      setFormError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewWorkflowChange = (field, value) => {
    setNewWorkflow(prev => ({
      ...prev,
      [field]: field === 'parserSegmentSize' ? (value === '' ? '' : parseInt(value, 10) || 0) : value,
      ...(field === 'parserType' && value !== ALTERNATIVE_PARSER
        ? { parserKey: '', parserSegmentSize: '' }
        : {}),
    }));
  };

  // Add debug log to see the rows data when it changes
  useEffect(() => {
    console.log('Current rows data:', rows);
    if (rows.length > 0) {
      console.log('First row structure:', rows[0]);
      console.log('Available fields in first row:', Object.keys(rows[0]));
    }
  }, [rows]);

  const columns = [
    { 
      field: 'documentTypeName', 
      headerName: 'Document Type Name', 
      flex: 1,
      valueGetter: (params) => {
        // Try different possible field names from the API response
        const value = params.row.documentTypeName || params.row.name || 'N/A';
        return value;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<InfoIcon sx={{ color: ht.text, fontSize: 20 }} />}
            label="View Workflow Info"
            onClick={() => handleWorkflowInfoClick(params.row)}
            showInMenu={false}
          />,
        ];

        if (activeTab === 'REF' && canDelete) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon color="error" />}
              label="Delete"
              onClick={() => handleDeleteDocTypeConfig(params.row)}
              showInMenu={false}
            />
          );
        }

        return actions;
      },
    }
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 600, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Tenant Details</Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'docTypes'}
          onChange={() => setExpanded(expanded === 'docTypes' ? false : 'docTypes')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Document Types</Typography>
            <IconButton onClick={e => { e.stopPropagation(); handleOpenDialog(); }} size="small" disabled={!canAdd} sx={harmonateDrawerAddIconSx}>
              <AddIcon fontSize="small" />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails sx={harmonateDrawerAccordionDetailsSx}>
            <Box sx={{ width: '100%' }}>
              <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="document type tabs"
                  sx={harmonateTabsSx}
                >
                  <Tab label="Workflow (WF)" value="WF" />
                  <Tab label="Reference (REF)" value="REF" />
                  <Tab label="Credit Card (CC)" value="CC" />
                  <Tab label="General Ledger (GL)" value="GL" />
                  <Tab label="Treasury (TR)" value="TR" />
                </Tabs>
              </Paper>
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : (
                <Box sx={{ height: 450, width: '100%' }}>
                  {console.log('Rendering DataGrid with rows:', rows)}
                  <DataGridPro
                    rows={rows}
                    columns={columns}
                    pagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    pageSize={10}
                    autoPageSize={false}
                    columnResizeMode="onEnd"
                    resizable
                    disableSelectionOnClick
                    disableColumnMenu
                    getRowId={(row) => {
                      // Use documentTypeId if available, otherwise fall back to id or generate a random one
                      const id = row.documentTypeId || row.id || `row-${Math.random().toString(36).substr(2, 9)}`;
                      console.log('Row ID:', id, 'Row data:', row);
                      return id;
                    }}
                    sx={{
                      ...harmonateDataGridSx,
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          cursor: 'pointer',
                          backgroundColor: ht.listBg,
                        },
                      },
                    }}
                    onRowClick={(params) => {
                      console.log('Row clicked:', params.row);
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Typography>No document types found</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'users'}
          onChange={() => setExpanded(expanded === 'users' ? false : 'users')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Users</Typography>
            <IconButton onClick={e => { e.stopPropagation(); handleOpenUserDialog(); }} size="small" disabled={!canAdd} sx={harmonateDrawerAddIconSx}>
              <AddIcon fontSize="small" />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails sx={{ ...harmonateDrawerAccordionDetailsSx, height: 300, overflow: 'hidden' }}>
            <UserDrawer tenantId={tenantId} refreshTrigger={userRefreshTrigger} />
          </AccordionDetails>
        </Accordion>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'glSystems'}
          onChange={() => setExpanded(expanded === 'glSystems' ? false : 'glSystems')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>GL Systems</Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                glSystemDrawerRef.current?.openEditDialog();
              }}
              size="small"
              disabled={!selectedGLSystemRow || !canEdit}
              sx={harmonateDrawerEditIconSx}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                glSystemDrawerRef.current?.openDeleteDialog();
              }}
              size="small"
              disabled={!selectedGLSystemRow || !canDelete}
              sx={harmonateDrawerDeleteIconSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                glSystemDrawerRef.current?.openAddDialog();
              }}
              size="small"
              disabled={!canAdd}
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails sx={{ ...harmonateDrawerAccordionDetailsSx, height: 320, overflow: 'hidden' }}>
            <GLSystemDrawer
              ref={glSystemDrawerRef}
              tenantId={tenantId}
              refreshTrigger={false}
              hideToolbar
              onSelectionChange={setSelectedGLSystemRow}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'workforce'}
          onChange={() => setExpanded(expanded === 'workforce' ? false : 'workforce')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Workforce</Typography>
          </AccordionSummary>
          <AccordionDetails sx={harmonateDrawerAccordionDetailsSx}>
            <TenantWorkforceWeekSection
              open={open && expanded === 'workforce'}
              tenantId={tenantId}
              onSaved={(message) => showSnackbar(message, 'success')}
            />
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Document Type</DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              select
              label="Document Type"
              name="documentType"
              value={selectedDocType}
              onChange={e => setSelectedDocType(e.target.value)}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              {docTypes.map(dt => (
                <MenuItem key={dt.id} value={dt.id}>{dt.name}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting || !selectedDocType} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add User</DialogTitle>
        <form onSubmit={handleSaveUser}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField 
              label="Email" 
              name="email" 
              value={userForm.email} 
              onChange={handleUserFormChange} 
              required 
              fullWidth
              size="small"
              helperText="This will also be used as your username"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField 
              label="First Name" 
              name="first_name" 
              value={userForm.first_name} 
              onChange={handleUserFormChange} 
              required 
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField 
              label="Last Name" 
              name="last_name" 
              value={userForm.last_name} 
              onChange={handleUserFormChange} 
              required 
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseUserDialog} disabled={userSubmitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={userSubmitting} sx={harmonateProceedButtonSx}>
              {userSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Workflow Info Dialog */}
      <Dialog
        open={workflowDialogOpen}
        onClose={handleCloseWorkflowDialog}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="workflow-dialog-title"
      >
        <DialogTitle id="workflow-dialog-title" sx={harmonateDialogTitleSx}>
          <Box component="span" sx={{ flex: 1, pr: 1 }}>
            Workflow Details
            {selectedDocumentType && ` - ${selectedDocumentType.documentTypeName || selectedDocumentType.name || ''}`}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            disabled={!canAdd}
            sx={harmonateBlueButtonSx}
          >
            Add Workflow
          </Button>
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {workflowLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : workflowError ? (
            <Alert severity="error">
              Failed to load workflow data: {workflowError}
            </Alert>
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGridPro
                  rows={workflowData}
                  columnResizeMode="onEnd"
                  resizable
                  columns={[
                    { 
                      field: 'workflowDefinitionName', 
                      headerName: 'Workflow Name', 
                      minWidth: 200,
                      flex: 1,
                      valueGetter: (params) => params.row.workflowDefinitionName || 'N/A'
                    },
                    { 
                      field: 'parserType', 
                      headerName: 'Parser Type', 
                      minWidth: 150,
                      valueGetter: (params) => parserTypeLabel(params.row.parserType)
                    },
                    { 
                      field: 'parserKey', 
                      headerName: 'Parser Key', 
                      minWidth: 150,
                      valueGetter: (params) => params.row.parserKey || 'N/A'
                    },
                    { 
                      field: 'parserSegmentSize', 
                      headerName: 'Segment Size', 
                      minWidth: 120,
                      type: 'number',
                      align: 'right',
                      headerAlign: 'right',
                      valueGetter: (params) => params.row.parserSegmentSize || 'N/A'
                    },
                    { 
                      field: 'actions',
                      type: 'actions',
                      headerName: 'Actions',
                      width: 150,
                      headerAlign: 'center',
                      align: 'center',
                      getActions: (params) => [
                        ...(canEdit ? [
                        <GridActionsCellItem
                          icon={<EditOutlinedIcon sx={{ color: ht.text, fontSize: 20 }} />}
                          label="Edit"
                          onClick={() => handleEditWorkflow(params.row)}
                        />
                        ] : []),
                        ...(canDelete ? [
                        <GridActionsCellItem
                          icon={<DeleteIcon color="error" />}
                          label="Delete"
                          onClick={() => handleDeleteWorkflow(params.row.id)}
                        />
                        ] : []),
                      ],
                    },
                  ]}
                  autoHeight
                  pagination
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  autoPageSize={false}
                  disableSelectionOnClick
                  getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
                  sx={{
                    ...harmonateDataGridSx,
                    '& .MuiDataGrid-cell': {
                      padding: '0 16px',
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseWorkflowDialog} variant="outlined" sx={harmonateCancelButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Workflow Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="add-workflow-dialog-title"
      >
        <form onSubmit={handleAddWorkflow}>
          <DialogTitle id="add-workflow-dialog-title" sx={harmonateDialogTitleSx}>
            Add New Workflow
          </DialogTitle>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {formError && (
              <Alert severity="error">
                {formError}
              </Alert>
            )}
            <TextField
              select
              label="Workflow Definition"
              value={newWorkflow.workflowDefinitionSnowflakeId}
              onChange={(e) => handleNewWorkflowChange('workflowDefinitionSnowflakeId', e.target.value)}
              fullWidth
              size="small"
              required
              error={!newWorkflow.workflowDefinitionSnowflakeId}
              helperText={!newWorkflow.workflowDefinitionSnowflakeId ? 'Workflow Definition is required' : ''}
              InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>Select Workflow</em>
              </MenuItem>
              {workflowDefinitions.map((workflow) => (
                <MenuItem key={workflow.id} value={workflow.id}>
                  {workflow.name || workflow.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Parser Type"
              value={newWorkflow.parserType}
              onChange={(e) => handleNewWorkflowChange('parserType', e.target.value)}
              fullWidth
              size="small"
              required
              error={!newWorkflow.parserType}
              helperText={!newWorkflow.parserType ? 'Parser Type is required' : ''}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>Select Parser Type</em>
              </MenuItem>
              <MenuItem value={DOC_PARSER}>DocParser</MenuItem>
              <MenuItem value={ALTERNATIVE_PARSER}>Alternative Parser</MenuItem>
            </TextField>
            {isAlternativeParser(newWorkflow.parserType) && (
              <>
                <TextField
                  label="Parser Key"
                  value={newWorkflow.parserKey}
                  onChange={(e) => handleNewWorkflowChange('parserKey', e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!String(newWorkflow.parserKey || '').trim()}
                  helperText={!String(newWorkflow.parserKey || '').trim() ? 'Parser Key is required for Alternative Parser' : ''}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />
                <TextField
                  label="Segment Size"
                  type="number"
                  value={newWorkflow.parserSegmentSize}
                  onChange={(e) => handleNewWorkflowChange('parserSegmentSize', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Pages per segment (defaults to 1 if blank)"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                  InputProps={{
                    inputProps: { 
                      min: 1,
                      step: 1
                    }
                  }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setAddDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={
                submitting
                || !newWorkflow.workflowDefinitionSnowflakeId
                || !newWorkflow.parserType
                || (isAlternativeParser(newWorkflow.parserType) && !String(newWorkflow.parserKey || '').trim())
              }
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Workflow Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="edit-workflow-dialog-title"
      >
        <form onSubmit={handleSaveWorkflow}>
          <DialogTitle id="edit-workflow-dialog-title" sx={harmonateDialogTitleSx}>
            Edit Workflow
          </DialogTitle>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {editWorkflowData && (
              <>
                <TextField
                  label="Workflow Name"
                  value={editWorkflowData.workflowDefinitionName || ''}
                  fullWidth
                  size="small"
                  required
                  disabled
                  InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                  sx={harmonateTextFieldSx}
                />
                <TextField
                  select
                  label="Parser Type"
                  value={normalizeParserType(editWorkflowData.parserType)}
                  onChange={(e) => handleEditInputChange('parserType', e.target.value)}
                  fullWidth
                  size="small"
                  required
                  InputLabelProps={harmonateInputLabelProps}
                  SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                  sx={harmonateTextFieldSx}
                >
                  <MenuItem value={DOC_PARSER}>DocParser</MenuItem>
                  <MenuItem value={ALTERNATIVE_PARSER}>Alternative Parser</MenuItem>
                </TextField>
                {isAlternativeParser(editWorkflowData.parserType) && (
                  <>
                    <TextField
                      label="Parser Key"
                      value={editWorkflowData.parserKey || ''}
                      onChange={(e) => handleEditInputChange('parserKey', e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={!String(editWorkflowData.parserKey || '').trim()}
                      helperText={!String(editWorkflowData.parserKey || '').trim() ? 'Parser Key is required for Alternative Parser' : ''}
                      InputLabelProps={harmonateInputLabelProps}
                      sx={harmonateTextFieldSx}
                    />
                    <TextField
                      label="Segment Size"
                      type="number"
                      value={editWorkflowData.parserSegmentSize || ''}
                      onChange={(e) => handleEditInputChange('parserSegmentSize', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 1 }}
                      helperText="Pages per segment (defaults to 1 if blank)"
                      InputLabelProps={harmonateInputLabelProps}
                      sx={harmonateTextFieldSx}
                    />
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setEditDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={docTypeDeleteConfirmOpen}
        onClose={handleCancelDocTypeDelete}
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="delete-doc-type-confirmation-dialog"
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Are you sure you want to delete this document type configuration
            {docTypeConfigToDelete?.documentTypeName ? ` (${docTypeConfigToDelete.documentTypeName})` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelDocTypeDelete} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDocTypeConfig}
            variant="contained"
            autoFocus
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>Are you sure you want to delete this workflow?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelDelete} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteWorkflow}
            variant="contained"
            autoFocus
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ '& .MuiSnackbar-root': { zIndex: 9999 } }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            maxWidth: '400px',
            '& .MuiAlert-message': { whiteSpace: 'pre-line' }
          }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default DocumentTypesDrawer;